// ────────────────────────────────────────────────────────────────────────────
// main.ts — receiver entry point.
//
// Responsibilities (in execution order):
//   1. Boot CAF receiver context + get player manager.
//   2. Initialise cast.debug.CastDebugLogger; wire CAF event auto-logging.
//   3. Construct PlaybackMachine actor.
//   4. Install LOAD message interceptor.  Each LOAD:
//        - validates customData (token/url/itemId),
//        - stamps a monotonic __receiverEpoch into customData,
//        - constructs JellyfinClient + ProgressReporter,
//        - sends LOAD event to the machine,
//        - sets startPositionTicks on the CAF request,
//        - adopts the item's intro/outro segments + auto-skip prefs and, if
//          starting inside an intro with skip-intro on, bumps the start past it.
//   5. Subscribe CAF events → forward to machine, with a session-attribution
//      guard (cafEventIsStale) that drops events whose customData epoch
//      doesn't match the machine's current context.epoch.  This is the
//      defence against stale MEDIA_FINISHED / ERROR from prior sessions
//      during queue auto-advance.
//   6. Handle the urn:x-cast:com.projektor.debug custom messages (dev
//      commands — see DEV_COMMANDS.md) and urn:x-cast:com.projektor.control
//      `set-autoskip` (live auto-skip preference updates; flips raise a
//      bottom-left toast via showToast).
//   7. Auto-skip intros + auto-advance on outros off TIME_UPDATE
//      (maybeAutoSkip); see the auto-skip block.
//   8. context.start().
//
// Logging: cast.debug.CastDebugLogger singleton.  Tag constants below.
// App logs visible on-screen via showDebugLogs() or CaC Tool.
// CAF/Shaka internals visible in chrome://inspect.
//
// This file is a wiring module — no business logic beyond event dispatch
// and the LOAD interceptor.  All playback coordination lives in
// PlaybackMachine.ts; all subsystem behaviour in their own modules.
//
// Long pauses: ProgressReporter posts /Sessions/Playing/Progress every 10 s
// during pause (with IsPaused:true).  Per docs/chromecast-jellyfin-receiver-
// reference.md §16 this resets Jellyfin's HLS transcode kill timer, so
// ffmpeg stays alive for the duration of the pause and resume just works.
// No receiver-side reload / restart logic.
//
// Stall recovery: the decoder can wedge mid-playback ("stalled in buffered
// region") with no error event — currentTime freezes while data is still
// buffered ahead.  An independent setInterval watchdog (NOT the TIME_UPDATE
// handler, which goes silent during a freeze) polls getCurrentTimeSec() and,
// when the playhead is frozen past STALL_DETECT_MS while readyState shows data
// buffered ahead, nudges the playhead forward (video.currentTime, falling back
// to a CAF seek) — escalating, capped.  See the stall-detection block below.
// ────────────────────────────────────────────────────────────────────────────

import "./cast.d.ts"
import { JellyfinClient } from "./JellyfinClient.ts"
import { ProgressReporter } from "./ProgressReporter.ts"
import { type PlaybackEvent, playbackMachine } from "./PlaybackMachine.ts"
import { ErrorCodes, ErrorMessages, formatCafDetailedError } from "./ErrorCodes.ts"
import { createActor, type SnapshotFrom } from "xstate"
import type { CastSegment, ProjektorLoadData } from "./types.ts"

// ── Logging ───────────────────────────────────────────────────────────────

const castDebugLogger = cast.debug.CastDebugLogger.getInstance()
castDebugLogger.setEnabled(false)
castDebugLogger.showDebugLogs(false)

castDebugLogger.loggerLevelByEvents = {
  "cast.framework.events.category.CORE": cast.framework.LoggerLevel.INFO,
  "cast.framework.events.EventType.ERROR": cast.framework.LoggerLevel.ERROR,
  "cast.framework.events.EventType.MEDIA_ERROR": cast.framework.LoggerLevel.ERROR,
}

const TAG_BOOT = "BOOT"
const TAG_CAF = "CAF"
const TAG_LOAD = "LOAD"
const TAG_MACHINE = "MACHINE"
const TAG_DEV = "DEV"
const TAG_CONTROL = "CONTROL"
const TAG_STALL = "STALL"
const TAG_VIDEO = "VIDEO"
const TAG_SKIP = "SKIP"

// ── Ring-buffer logger ────────────────────────────────────────────────────
// CastDebugLogger's on-screen overlay is lost on reload / crash.  Keep a
// rotating in-memory buffer so we can dump the last ~60 s of playback state
// via a debug command or auto-dump it when the stall watchdog fires.
const MAX_BUFFERED_LOGS = 500
const logBuffer: string[] = []

function bufferedLog(
  level: "debug" | "info" | "warn" | "error",
  tag: string,
  message: string,
): void {
  const line = `[${new Date().toISOString().slice(11, 23)}] [${tag}] ${message}`
  logBuffer.push(line)
  if (logBuffer.length > MAX_BUFFERED_LOGS) {
    logBuffer.shift()
  }
  // Also emit to CastDebugLogger so the CaC Tool / overlay sees it
  switch (level) {
    case "debug":
      castDebugLogger.debug(tag, message)
      break
    case "info":
      castDebugLogger.info(tag, message)
      break
    case "warn":
      castDebugLogger.warn(tag, message)
      break
    case "error":
      castDebugLogger.error(tag, message)
      break
  }
}

function dumpLogBuffer(): void {
  console.log("========== RECEIVER LOG DUMP ==========")
  for (const line of logBuffer) {
    console.log(line)
  }
  console.log("========== END DUMP ==========")
}

globalThis.addEventListener("error", (ev) => {
  bufferedLog("error", TAG_BOOT, `${ev.message} @ ${ev.filename}:${ev.lineno}`)
})
globalThis.addEventListener("unhandledrejection", (ev) => {
  const detail = ev.reason instanceof Error
    ? `${ev.reason.message} ${ev.reason.stack ?? ""}`
    : String(ev.reason)
  bufferedLog("error", TAG_BOOT, `Unhandled: ${detail}`)
})

// ── CAF context ───────────────────────────────────────────────────────────

const context = cast.framework.CastReceiverContext.getInstance()
const playerManager = context.getPlayerManager()

// SDK internal log verbosity.  Kept at ERROR (not VERBOSE) because CAF's
// internal logger retains messages in memory on the receiver device;
// VERBOSE produced 100+ lines per minute and is suspected of contributing
// to renderer-page wedges on Chromecast Ultra (~512MB usable RAM).  We
// have not extracted useful diagnostics from the verbose output across
// 8+ weeks of debugging — the cost-benefit is clear.
context.setLoggerLevel(cast.framework.LoggerLevel.ERROR)

// Build stamp — src/build-info.ts is regenerated by build.ts on every build.
import { BUILD_ID, BUILD_TS } from "./build-info.ts"
bufferedLog(
  "info",
  TAG_BOOT,
  `receiver bundle #${BUILD_ID} builtAt=${BUILD_TS} ua=${
    globalThis.navigator?.userAgent ?? "unknown"
  }`,
)

// Stamp the build into the idle-screen overlay (#build-info in index.html).
// Visibility is toggled by lifecycle in the machine subscription below — shown
// only while IDLE so it never overlaps playing video.
const buildInfoEl = document.getElementById("build-info")
if (buildInfoEl) buildInfoEl.textContent = `#${BUILD_ID} · ${BUILD_TS}`

// Settings-change toast (#toast in index.html). Shown when a set-autoskip
// control message flips a preference, so the viewer sees the change took effect.
// Auto-dismiss is a CSS animation; restart it by clearing the class and forcing
// a reflow. No JS timer — keeps the receiver's UI strictly event-driven.
const toastEl = document.getElementById("toast")
function showToast(text: string): void {
  if (!toastEl) return
  toastEl.textContent = text
  toastEl.classList.remove("show")
  void toastEl.offsetWidth // force reflow so the animation re-runs
  toastEl.classList.add("show")
}

// crypto.randomUUID() requires Chrome 92; Chromecast cast shell targets Chrome 80.
// Use getRandomValues() instead (Chrome 37+).
function uuid(): string {
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, "0"))
  return `${h.slice(0, 4).join("")}-${h.slice(4, 6).join("")}-${h.slice(6, 8).join("")}-${
    h.slice(8, 10).join("")
  }-${h.slice(10).join("")}`
}

// ── Helpers ────────────────────────────────────────────────────────────────

function currentPositionTicks(): number {
  return Math.round(playerManager.getCurrentTimeSec() * 10_000_000)
}

// ── Auto-skip (intro/outro media segments) ──────────────────────────────────
//
// The receiver auto-skips intros and advances to the next queue item on outros
// off its own TIME_UPDATE — no new timer, event-driven.  Two inputs:
//   • Segments: per-item, ride in LOAD customData (`data.segments`).  Replaced
//     on every LOAD and tagged with that load's epoch so a stale item's spans
//     can never drive a skip.
//   • Prefs: global toggles + per-type lead delays.  Seeded from customData at
//     LOAD (so the start-position bump can fire before any message arrives), then
//     owned by the control channel once it sends `set-autoskip` (`prefsFromControl`).
//     Control wins so a live change isn't clobbered by a stale queued item's
//     snapshot on auto-advance.
// Delays are a per-type lead offset in seconds: fire the skip at segment.start +
// delay (not at the edge) so a few frames play and the cut isn't violent.
const autoSkipPrefs = {
  skipIntro: false,
  skipOutroNext: false,
  introDelaySec: 0,
  outroDelaySec: 0,
  prefsFromControl: false,
}

let activeSegments: CastSegment[] = []
let activeSegmentsEpoch = -1
// Intro spans already skipped this load (keyed by start tick), so a user
// seeking back into one doesn't get yanked forward again.
const consumedIntros = new Set<number>()
// Outro spans already acted on this load (keyed by start tick), so we trigger
// the advance once and don't re-fire while the playhead sits in the outro.
const consumedOutros = new Set<number>()
// Tracks whether the playhead was inside an intro/outro last tick, so we log
// only on enter/leave transitions instead of every TIME_UPDATE.
let lastInIntro = false
let lastInOutro = false

function ticksToSec(ticks: number): number {
  return ticks / 10_000_000
}

/** The intro segment containing `sec`, if any. */
function introContaining(sec: number): CastSegment | undefined {
  return activeSegments.find(
    (s) => s.type === "Intro" && sec >= ticksToSec(s.startTicks) && sec < ticksToSec(s.endTicks),
  )
}

/** The outro segment containing `sec`, if any. */
function outroContaining(sec: number): CastSegment | undefined {
  return activeSegments.find(
    (s) => s.type === "Outro" && sec >= ticksToSec(s.startTicks) && sec < ticksToSec(s.endTicks),
  )
}

/** Whether the CAF queue has an item after the one playing.  Read-only — CAF's
 *  QueueManager has no programmatic advance, so outro auto-advance fires via
 *  seek-to-end (native MEDIA_FINISHED auto-advance) and is gated on this so the
 *  last episode's outro plays out instead of dead-ending. */
function hasNextQueueItem(): boolean {
  const qm = playerManager.getQueueManager()
  if (!qm) return false
  const items = qm.getItems()
  if (!Array.isArray(items) || items.length === 0) return false
  const idx = qm.getCurrentItemIndex()
  if (typeof idx !== "number" || idx < 0) return false
  return idx < items.length - 1
}

/** Adopt a freshly-loaded item's segments + seed prefs.  Called from the LOAD
 *  interceptor before playback starts. */
function adoptSegments(data: ProjektorLoadData, epoch: number): void {
  activeSegments = Array.isArray(data.segments) ? data.segments : []
  activeSegmentsEpoch = epoch
  consumedIntros.clear()
  consumedOutros.clear()
  if (!autoSkipPrefs.prefsFromControl) {
    if (typeof data.autoSkipIntro === "boolean") autoSkipPrefs.skipIntro = data.autoSkipIntro
    if (typeof data.autoSkipOutroNext === "boolean") {
      autoSkipPrefs.skipOutroNext = data.autoSkipOutroNext
    }
    if (typeof data.autoSkipIntroDelaySec === "number") {
      autoSkipPrefs.introDelaySec = data.autoSkipIntroDelaySec
    }
    if (typeof data.autoSkipOutroDelaySec === "number") {
      autoSkipPrefs.outroDelaySec = data.autoSkipOutroDelaySec
    }
  }
  const windows = activeSegments
    .filter((s) => s.type === "Intro")
    .map((s) => `${ticksToSec(s.startTicks).toFixed(1)}-${ticksToSec(s.endTicks).toFixed(1)}`)
    .join(",")
  castDebugLogger.info(
    TAG_SKIP,
    `adopt: segs=${activeSegments.length} intros=[${windows}] ` +
      `skipIntro=${autoSkipPrefs.skipIntro} fromControl=${autoSkipPrefs.prefsFromControl} epoch=${epoch}`,
  )
}

/** Drive auto-skip from a position update.  Intro → seek past it; outro →
 *  seek to end of file so CAF's native end-of-media handler auto-advances the
 *  queue (CAF exposes no programmatic advance — see hasNextQueueItem). */
function maybeAutoSkip(currentSec: number | undefined): void {
  if (currentSec === undefined || !Number.isFinite(currentSec)) return
  if (!isMachinePlaying()) return
  // Only act on the item these segments belong to.
  if (playbackActor.getSnapshot().context.epoch !== activeSegmentsEpoch) return

  if (autoSkipPrefs.skipIntro) maybeSkipIntro(currentSec)
  if (autoSkipPrefs.skipOutroNext) maybeAdvanceOnOutro(currentSec)
}

/** Seek past an intro containing the playhead, once per load. Fires at the intro
 *  start + the configured lead delay (not at the edge), so a few frames play. */
function maybeSkipIntro(currentSec: number): void {
  const intro = introContaining(currentSec)
  // Log only on intro enter/leave, not every tick — no console spam.
  const inIntro = intro !== undefined
  if (inIntro !== lastInIntro) {
    lastInIntro = inIntro
    castDebugLogger.info(
      TAG_SKIP,
      `${inIntro ? "entered" : "left"} intro at ${currentSec.toFixed(1)}s`,
    )
  }
  if (!intro || consumedIntros.has(intro.startTicks)) return
  // Lead delay: hold until start+delay so the cut isn't a violent edge-frame jump.
  // Tick-driven — fires on the first TIME_UPDATE at/after the threshold.
  if (currentSec < ticksToSec(intro.startTicks) + autoSkipPrefs.introDelaySec) return

  const to = ticksToSec(intro.endTicks)
  if (!Number.isFinite(to)) return
  // PlayerManager.seek() takes a number of seconds — NOT a SeekRequestData.
  // Passing an object coerces to NaN and CAF throws "Failed to set currentTime
  // ... non-finite". This is the documented programmatic-seek API.
  playerManager.seek(to)
  consumedIntros.add(intro.startTicks)
  castDebugLogger.info(TAG_SKIP, `auto-skip intro ${currentSec.toFixed(1)}s → ${to.toFixed(1)}s`)
}

/** On entering an outro, seek to END OF FILE to fire MEDIA_FINISHED → CAF native
 *  queue auto-advance.  No next item → let the outro play out (last episode).
 *  Fires once per outro span (consumedOutros) so credits-watching isn't fought. */
function maybeAdvanceOnOutro(currentSec: number): void {
  const outro = outroContaining(currentSec)
  // Log only on outro enter/leave, not every tick — no console spam.
  const inOutro = outro !== undefined
  if (inOutro !== lastInOutro) {
    lastInOutro = inOutro
    castDebugLogger.info(
      TAG_SKIP,
      `${inOutro ? "entered" : "left"} outro at ${currentSec.toFixed(1)}s`,
    )
  }
  if (!outro || consumedOutros.has(outro.startTicks)) return
  // Lead delay: hold on the current episode until start+delay before advancing.
  if (currentSec < ticksToSec(outro.startTicks) + autoSkipPrefs.outroDelaySec) return

  // Mark consumed regardless of branch — we act (or decline to) exactly once.
  consumedOutros.add(outro.startTicks)
  if (!hasNextQueueItem()) {
    castDebugLogger.info(TAG_SKIP, `outro at ${currentSec.toFixed(1)}s, no next item — playing out`)
    return
  }
  // Decision: advance by seeking to END OF FILE, not the outro's end. An outro can
  // end before true EOF (post-credits, or IntroSkipper marking the span short);
  // seeking there lands mid-file and never fires MEDIA_FINISHED, so the queue never
  // advances. Seek to the media duration so CAF's native end-of-media handler runs —
  // the same path as an episode playing out.
  const eof = playerManager.getMediaInformation()?.duration
  if (eof === undefined || !Number.isFinite(eof)) {
    castDebugLogger.warn(TAG_SKIP, `outro at ${currentSec.toFixed(1)}s — no duration, cannot advance`)
    return
  }
  playerManager.seek(eof)
  castDebugLogger.info(TAG_SKIP, `auto-advance on outro ${currentSec.toFixed(1)}s → seek EOF ${eof.toFixed(1)}s`)
}

// ── Playback state machine ─────────────────────────────────────────────────

const playbackActor = createActor(playbackMachine)

let lastLifecycle = ""
let lastPlayback = ""

// Subscribe to state changes for non-async side effects and debug logging.
playbackActor.subscribe((snapshot: SnapshotFrom<typeof playbackMachine>) => {
  const value = snapshot.value as { lifecycle: string; playback: string }
  const lifecycle = value.lifecycle
  const playback = value.playback
  const ctx = snapshot.context

  if (lifecycle !== lastLifecycle) {
    lastLifecycle = lifecycle
    castDebugLogger.info(
      TAG_MACHINE,
      `lifecycle → ${lifecycle} item=${ctx.activeSession?.data.jellyfinItemID ?? "—"} err=${
        ctx.lastError ?? "—"
      }`,
    )
    // Build stamp shows only on the idle screen (no media loaded).
    if (buildInfoEl) buildInfoEl.style.display = lifecycle === "IDLE" ? "block" : "none"
  }
  if (playback !== lastPlayback) {
    // Progress reporter only cares about play/pause once session is live.
    if (lifecycle === "READY") {
      if (playback === "PAUSED") ctx.progressReporter?.onPause()
      if (playback === "PLAYING") ctx.progressReporter?.onResume?.()
    }
    lastPlayback = playback
    bufferedLog("info", TAG_MACHINE, `playback → ${playback}`)
  }
})

playbackActor.start()

// ── Session-attribution filter ─────────────────────────────────────────────
//
// CAF fires events on wall-clock time and does not tag them with a session id.
// When CAF auto-advances the queue after a stream error (or any time we load a
// replacement item), events for the prior stream (ERROR, MEDIA_FINISHED, etc.)
// can arrive AFTER our new LOAD has been processed.  Those would bounce the
// machine into ERROR / IDLE for the wrong item.
//
// Fix: every LOAD bumps a monotonic `loadEpoch` counter and stamps it into the
// request's customData (`__receiverEpoch`).  CAF keeps that stamp on
// mediaInformation across event dispatch, so every handler can compare the
// in-flight event's epoch against the epoch the machine thinks is active.
// Mismatch → drop + log.  This supersedes the earlier itemId-based guard,
// which misfired when the same item was reloaded back-to-back.
let loadEpoch = 0
const EPOCH_KEY = "__receiverEpoch"

function cafEventIsStale(label: string): boolean {
  const ctx = playbackActor.getSnapshot().context
  if (ctx.activeSession == null) return false // no session yet; let machine decide
  const info = playerManager.getMediaInformation()
  const stamped = info?.customData as (ProjektorLoadData & { [EPOCH_KEY]?: number }) | undefined
  const infoEpoch = stamped?.[EPOCH_KEY]
  if (infoEpoch !== undefined && infoEpoch !== ctx.epoch) {
    castDebugLogger.warn(
      TAG_CAF,
      `${label} dropped — stale epoch=${infoEpoch} current=${ctx.epoch} item=${
        stamped?.jellyfinItemID ?? "—"
      }`,
    )
    return true
  }
  return false
}

// ── Stall detection + recovery ───────────────────────────────────────────────
//
// THE BUG: the Chromecast Ultra's HW H.264 decoder can wedge mid-stream —
// currentTime freezes while data is still buffered AHEAD of the playhead
// (readyState stays HAVE_FUTURE_DATA / HAVE_ENOUGH_DATA).  No ERROR, no
// MEDIA_ERROR, no PAUSE.  This is a *decoder* stall, not a buffer underrun.
// Default MPL (unlike Shaka) ships no gap-jumping / stall recovery, so the
// playhead stays frozen until the device is power-cycled.  Observed mid-episode
// 2026-04-18 and again 2026-05-15 (~100s frozen, never self-recovered).
//
// WHY AN INDEPENDENT INTERVAL, NOT THE TIME_UPDATE HANDLER: TIME_UPDATE is
// driven by the media element's `timeupdate`, which only fires while
// currentTime is *changing*.  During a hard freeze it goes silent — a watchdog
// living inside TIME_UPDATE can never observe the freeze it is meant to catch
// (the prior detection had exactly this hole).  We poll getCurrentTimeSec() on
// a fixed wall-clock cadence instead.  CLAUDE.md bans timers for UI; this is
// backend playback logic, same exemption as ProgressReporter's poll.
//
// RECOVERY = forward nudge, escalating + capped (cf. hls.js nudgeMaxRetry:
// uncapped nudging hangs at discontinuities — hls.js#1055 / #5873).  Two tiers,
// because it is not yet proven on-device which one the Chromecast honours:
//   tiers 1-2  poke video.currentTime directly (Shaka gap_jumping_controller /
//              hls.js bufferNudgeOnStall style — light, no pipeline churn)
//   tiers 3-4  fall back to a CAF playerManager.seek() in case CAF ignores
//              direct element writes (heavier; re-buffers)
// When the <video> is unreachable behind CAF's shadow DOM (observed on device:
// "no <video>"), element-poke is impossible — EVERY attempt uses the CAF seek,
// the only lever left, and the readyState gate is skipped.
// A prior seek()-based attempt crashed when getCurrentTimeSec() returned NaN
// mid-seek, so EVERY position read is finite-guarded and falls back to the raw
// <video>.currentTime (see readPosSec) — both for detection and the seek
// target.  Recovery functions never throw.
//
// GATING: only recover when readyState >= HAVE_FUTURE_DATA (data buffered past
// the playhead → genuine decoder stall recovery can fix).  Lower readyState is
// a network underrun — a nudge/seek won't help, so we log and wait.
const WATCHDOG_INTERVAL_MS = 2_000 // how often we sample the playhead
const STALL_DETECT_MS = 8_000 // no advance this long while PLAYING = stall
const RECOVERY_RETRY_MS = 3_000 // min gap between nudge attempts
const NUDGE_MAX_RETRY = 4 // nudges per stall episode before giving up
const NUDGE_BASE_SEC = 0.1 // element-poke: attempt N nudges forward N * this
const SEEK_BASE_SEC = 0.3 // CAF-seek: attempt N seeks forward N * this
const HEARTBEAT_MS = 10_000 // healthy-state buffer-snapshot cadence
const HAVE_FUTURE_DATA = 3 // HTMLMediaElement.readyState threshold

let lastPosSec = 0
let lastAdvanceWallMs = 0
let lastNudgeWallMs = 0
let recoveryAttempts = 0
let stallActive = false
let lastHeartbeatMs = 0

function isMachinePlaying(): boolean {
  const v = playbackActor.getSnapshot().value as { lifecycle: string; playback: string }
  return v.lifecycle === "READY" && v.playback === "PLAYING"
}

function videoEl(): HTMLVideoElement | null {
  const direct = document.querySelector("video") as HTMLVideoElement | null
  if (direct) return direct
  // CAF renders into <cast-media-player>; the real <video> lives inside its
  // shadow DOM, invisible to a top-level querySelector (observed on device:
  // "no <video>").  Pierce it when the shadow root is open.
  const cmp = document.querySelector("cast-media-player")
  return (cmp?.shadowRoot?.querySelector("video") as HTMLVideoElement | null) ?? null
}

function describeVideo(v: HTMLVideoElement): string {
  let buffered = ""
  for (let i = 0; i < v.buffered.length; i++) {
    buffered += `${v.buffered.start(i).toFixed(1)}-${v.buffered.end(i).toFixed(1)} `
  }
  const ct = Number.isFinite(v.currentTime) ? v.currentTime.toFixed(2) : "NaN"
  return `ct=${ct} readyState=${v.readyState} buffered=[${buffered.trim()}] paused=${v.paused} ended=${v.ended} err=${
    v.error?.code ?? "none"
  }`
}

// Best-available playhead position in seconds, or NaN.  Prefer CAF's clock;
// fall back to the raw <video> element when getCurrentTimeSec() returns NaN
// (it can in transient states — that NaN crashed the prior recovery attempt).
function readPosSec(): number {
  const caf = playerManager.getCurrentTimeSec()
  if (Number.isFinite(caf)) return caf
  const v = videoEl()
  return v && Number.isFinite(v.currentTime) ? v.currentTime : NaN
}

// Tier 1-2 recovery: poke video.currentTime directly (Shaka/hls.js style,
// light — no pipeline churn).  Returns whether it acted; never throws.
function nudgePlayhead(attempt: number): boolean {
  const v = videoEl()
  if (!v) {
    bufferedLog("warn", TAG_STALL, "nudge skipped — no <video> element")
    return false
  }
  const from = v.currentTime
  if (!Number.isFinite(from)) {
    bufferedLog("warn", TAG_STALL, "nudge skipped — currentTime NaN (transient state)")
    return false
  }
  const to = from + NUDGE_BASE_SEC * attempt // 0.1, 0.2 …
  try {
    v.currentTime = to
  } catch (e) {
    bufferedLog("error", TAG_STALL, `nudge threw: ${e instanceof Error ? e.message : String(e)}`)
    return false
  }
  bufferedLog(
    "warn",
    TAG_STALL,
    `nudge #${attempt}: ${from.toFixed(2)}s → ${to.toFixed(2)}s ${describeVideo(v)}`,
  )
  return true
}

// Tier 3-4 recovery: CAF seek.  Heavier hammer for when CAF ignores direct
// element writes.  Finite-guarded (the prior seek attempt crashed on NaN);
// never throws.
function seekNudge(attempt: number): boolean {
  const base = readPosSec()
  if (!Number.isFinite(base)) {
    bufferedLog("warn", TAG_STALL, "seek-nudge skipped — no finite position")
    return false
  }
  const to = base + SEEK_BASE_SEC * attempt // 0.3, 0.6, 0.9, 1.2 … always forward
  try {
    playerManager.seek(to)
  } catch (e) {
    bufferedLog(
      "error",
      TAG_STALL,
      `seek-nudge threw: ${e instanceof Error ? e.message : String(e)}`,
    )
    return false
  }
  bufferedLog("warn", TAG_STALL, `seek-nudge #${attempt}: ${base.toFixed(2)}s → ${to.toFixed(2)}s`)
  return true
}

// One recovery step.  Gates on readyState (only act when data is buffered ahead
// — a real decoder stall, not a network underrun), then escalates by tier.
// Returns whether it actually acted (so the caller only burns retry budget on
// real attempts).
function recoverStall(attempt: number): boolean {
  const v = videoEl()
  if (!v) {
    // No reachable <video> (CAF shadow root closed): can't poke the element or
    // read readyState — CAF seek is the only lever.  Detection already
    // confirmed a real freeze, so act every attempt.
    return seekNudge(attempt)
  }
  if (v.readyState < HAVE_FUTURE_DATA) {
    bufferedLog(
      "warn",
      TAG_STALL,
      `no recovery — readyState=${v.readyState} (underrun, not decoder stall) ${describeVideo(v)}`,
    )
    return false
  }
  return attempt <= 2 ? nudgePlayhead(attempt) : seekNudge(attempt)
}

// Watchdog tick.  Drives detection + escalating capped recovery.  Runs on a
// fixed interval so it keeps firing even when TIME_UPDATE has gone silent.
function stallWatchdogTick(): void {
  const now = Date.now()
  const pos = readPosSec()

  // Not playing, or no valid position yet → reset baseline, nothing to watch.
  if (!isMachinePlaying() || !Number.isFinite(pos) || pos <= 0) {
    lastPosSec = Number.isFinite(pos) ? pos : 0
    lastAdvanceWallMs = now
    recoveryAttempts = 0
    stallActive = false
    return
  }

  // Playhead advanced since last sample → healthy.
  if (pos > lastPosSec) {
    if (stallActive) {
      bufferedLog(
        "warn",
        TAG_STALL,
        `recovered at ${pos.toFixed(2)}s after ${recoveryAttempts} attempt(s)`,
      )
    }
    lastPosSec = pos
    lastAdvanceWallMs = now
    recoveryAttempts = 0
    stallActive = false
    // Periodic snapshot so we have the buffer trend leading INTO a freeze.
    if (now - lastHeartbeatMs >= HEARTBEAT_MS) {
      lastHeartbeatMs = now
      const v = videoEl()
      if (v) bufferedLog("debug", TAG_VIDEO, `t=${pos.toFixed(1)} ${describeVideo(v)}`)
    }
    return
  }

  // No advance.  Within tolerance (normal jitter / short rebuffer)?
  const frozenMs = now - lastAdvanceWallMs
  if (frozenMs < STALL_DETECT_MS) return

  // Stall confirmed — log + dump once per episode.
  if (!stallActive) {
    stallActive = true
    const v = videoEl()
    bufferedLog(
      "error",
      TAG_STALL,
      `STALL at ${pos.toFixed(2)}s — frozen ${
        (frozenMs / 1000).toFixed(1)
      }s (machine=READY/PLAYING) ${v ? describeVideo(v) : "no <video>"}`,
    )
    dumpLogBuffer()
  }

  // Recovery: escalating, spaced by RECOVERY_RETRY_MS, capped.
  if (recoveryAttempts >= NUDGE_MAX_RETRY) {
    if (recoveryAttempts === NUDGE_MAX_RETRY) {
      recoveryAttempts++ // bump past cap so this fires exactly once
      bufferedLog(
        "error",
        TAG_STALL,
        `recovery FAILED after ${NUDGE_MAX_RETRY} attempts — still frozen at ${
          pos.toFixed(2)
        }s; giving up (power-cycle territory)`,
      )
      dumpLogBuffer()
    }
    return
  }
  if (now - lastNudgeWallMs < RECOVERY_RETRY_MS) return
  lastNudgeWallMs = now // space next evaluation even if this one was gated (no spam)
  if (recoverStall(recoveryAttempts + 1)) recoveryAttempts++
}

// ── Load interceptor ───────────────────────────────────────────────────────

playerManager.setMessageInterceptor(
  cast.framework.messages.MessageType.LOAD,
  (request: cast.framework.messages.LoadRequestData) => {
    const data = request.media?.customData as ProjektorLoadData | undefined

    castDebugLogger.debug(
      TAG_LOAD,
      `LOAD intercepted contentId=${request.media?.contentId ?? "—"} itemId=${
        data?.jellyfinItemID ?? "—"
      } startTicks=${data?.startPositionTicks ?? 0}`,
    )

    // Validate all required customData
    if (!data?.token) {
      const code = ErrorCodes.LOAD_DATA_TOKEN_MISSING
      bufferedLog("error", TAG_LOAD, `${code}: ${ErrorMessages[code]}`)
      return request
    }
    if (!data?.serverUrl) {
      const code = ErrorCodes.LOAD_DATA_URL_MISSING
      bufferedLog("error", TAG_LOAD, `${code}: ${ErrorMessages[code]}`)
      return request
    }
    if (!data?.jellyfinItemID) {
      const code = ErrorCodes.LOAD_DATA_ITEM_ID_MISSING
      bufferedLog("error", TAG_LOAD, `${code}: ${ErrorMessages[code]}`)
      return request
    }

    const client = new JellyfinClient(data.serverUrl, data.token)

    const playSessionId = data.playSessionID ?? uuid()
    const epoch = ++loadEpoch // Stamp the epoch into the request's customData before returning, so CAF
     // echoes it back on every subsequent mediaInformation read. Events whose
    // mediaInformation.customData.__receiverEpoch < ctx.epoch are stale.
    ;(data as ProjektorLoadData & { [EPOCH_KEY]?: number })[EPOCH_KEY] = epoch

    const progressReporter = new ProgressReporter(
      client,
      currentPositionTicks,
      { data, playSessionId },
    )

    playbackActor.send({
      type: "LOAD",
      data,
      client,
      progressReporter,
      playSessionId,
      epoch,
    } as PlaybackEvent)

    const snap = playbackActor.getSnapshot().value as { lifecycle: string; playback: string }
    castDebugLogger.debug(
      TAG_LOAD,
      `machine after LOAD lifecycle=${snap.lifecycle} playback=${snap.playback} epoch=${epoch}`,
    )

    if (data.startPositionTicks > 0) {
      request.currentTime = data.startPositionTicks / 10_000_000
      bufferedLog("debug", TAG_LOAD, `startTime set: ${request.currentTime.toFixed(2)}s`)
    }

    // Auto-skip: adopt this item's segments + seed prefs, then — if we'd start
    // inside an intro and skip-intro is on — begin playback past it.  This is a
    // pure optimization to avoid the intro briefly showing on resume/0:00; it
    // does NOT mark the intro consumed, so if CAF ignores this start-position
    // write the TIME_UPDATE watcher still catches it. The watcher is the backstop.
    adoptSegments(data, epoch)
    if (autoSkipPrefs.skipIntro) {
      const startSec = data.startPositionTicks > 0 ? data.startPositionTicks / 10_000_000 : 0
      const intro = introContaining(startSec)
      // Only bump past the intro at LOAD if we'd already start past the lead-delay
      // window. Otherwise leave the start alone so the first N seconds play and the
      // TIME_UPDATE watcher does the skip at start+delay — bumping here would defeat
      // the delay on a fresh 0:00 start.
      if (intro && startSec >= ticksToSec(intro.startTicks) + autoSkipPrefs.introDelaySec) {
        request.currentTime = ticksToSec(intro.endTicks)
        castDebugLogger.info(
          TAG_SKIP,
          `start bumped past intro → ${request.currentTime.toFixed(2)}s`,
        )
      }
    }

    if (data.subtitleUrl) {
      if (!request.media.tracks) request.media.tracks = []
      request.media.tracks.push({
        trackId: 100,
        type: "TEXT",
        subtype: "SUBTITLES",
        trackContentId: data.subtitleUrl,
        trackContentType: "text/vtt",
      })
      request.activeTrackIds = [100]
      castDebugLogger.debug(
        TAG_LOAD,
        `subtitle track added codec=${data.subtitleCodec ?? "unknown"} url=${data.subtitleUrl}`,
      )
    }

    // Segment format is decided by the SENDER, not here.  Default profile emits
    // H.264 in MPEG-TS (HEVC sources are re-encoded to h264) and the iOS app
    // sets no hint → native MPL consumes TS fine.  The opt-in HEVC-remux profile
    // emits HEVC in fMP4/CMAF; there the iOS app sets `hlsVideoSegmentFormat =
    // FMP4` on the LOAD media info (CAF/MPL otherwise assumes TS and fails the
    // master with MANIFEST/411).  Native MPL plays 4K HEVC Main10 from fMP4
    // (SDR and HDR/PQ) — verified on the Cast Ultra.  Either way that hint rides
    // through this interceptor untouched.  Shaka stays disabled (see
    // context.start() below).

    return request
  },
)

// ── Playback event hooks ───────────────────────────────────────────────────
//
// Guard: some EventType values don't exist in all CAF versions (e.g. Chrome vs Chromecast).
// Accessing a missing key returns undefined, and addEventListener(undefined) throws uncaught
// which kills the rest of initialization including context.start(). Check every registration.
function on<T extends cast.framework.events.Event>(
  type: unknown,
  handler: (event: T) => void,
): void {
  if (type === undefined || type === null) {
    bufferedLog("warn", TAG_CAF, "Skipped addEventListener — event type undefined/null")
    return
  }
  playerManager.addEventListener(
    type as cast.framework.events.EventType,
    handler as (event: cast.framework.events.Event) => void,
  )
}

on(cast.framework.events.EventType.PLAYING, () => {
  if (cafEventIsStale("PLAYING")) return
  bufferedLog("debug", TAG_CAF, "PLAYING")
  playbackActor.send({ type: "PLAYING" } as PlaybackEvent)
})

on(cast.framework.events.EventType.PAUSE, () => {
  if (cafEventIsStale("PAUSE")) return
  bufferedLog("debug", TAG_CAF, "PAUSE")
  playbackActor.send({ type: "PAUSE" } as PlaybackEvent)
})

on(cast.framework.events.EventType.SEEKED, () => {
  if (cafEventIsStale("SEEKED")) return
  bufferedLog("debug", TAG_CAF, "SEEKED")
  // SEEKED doubles as queue-autoplay "first play" signal when lifecycle is
  // SESSION_READY — machine promotes to STARTING_PROGRESS.  Otherwise no-op.
  playbackActor.send({ type: "SEEKED" } as PlaybackEvent)
})

on(cast.framework.events.EventType.MEDIA_FINISHED, () => {
  if (cafEventIsStale("MEDIA_FINISHED")) return
  // Capture position at MEDIA_FINISHED so the Stopped report reflects where
  // the user actually left the item, not an up-to-10s-stale lastReportedTicks.
  const finalPositionTicks = currentPositionTicks()
  bufferedLog("debug", TAG_CAF, `MEDIA_FINISHED finalTicks=${finalPositionTicks}`)
  playbackActor.send({ type: "MEDIA_FINISHED", finalPositionTicks } as PlaybackEvent)
})

// ERROR / MEDIA_ERROR handlers log `detailedErrorCode` mapped to its symbolic
// name (see ErrorCodes.ts CafDetailedErrorNames) because the raw numeric code
// alone is useless for triage — this was added 2026-04-19 after we lost time
// to guessing that "102" meant transcoding-session-expired when it actually
// means MEDIA_DECODE.  If the skip-on-error bug recurs, the mapped name
// distinguishes transport (3xx/5xx) from decoder (1xx) from manifest (4xx).
on(cast.framework.events.EventType.ERROR, (event: cast.framework.events.ErrorEvent) => {
  const detailed = formatCafDetailedError(event.detailedErrorCode)
  const errorCode = ErrorCodes.CAF_ERROR_UNKNOWN_CODE
  castDebugLogger.error(
    TAG_CAF,
    `${errorCode}: ERROR ${detailed} "${event.errorDescription ?? ""}" t=${
      playerManager.getCurrentTimeSec().toFixed(1)
    }s`,
  )
  if (cafEventIsStale("ERROR")) return
  playbackActor.send({
    type: "ERROR",
    errorCode,
    errorDescription: `${detailed}: ${event.errorDescription || ""}`,
    error: `CAF Error - ${event.errorDescription || detailed}`,
  } as PlaybackEvent)
})

on(cast.framework.events.EventType.MEDIA_ERROR, (event: cast.framework.events.ErrorEvent) => {
  const detailed = formatCafDetailedError(event.detailedErrorCode)
  const errorCode = ErrorCodes.CAF_ERROR_UNKNOWN_CODE
  castDebugLogger.error(
    TAG_CAF,
    `${errorCode}: MEDIA_ERROR ${detailed} "${event.errorDescription ?? ""}" t=${
      playerManager.getCurrentTimeSec().toFixed(1)
    }s`,
  )
  if (cafEventIsStale("MEDIA_ERROR")) return
  playbackActor.send({
    type: "MEDIA_ERROR",
    errorCode,
    errorDescription: `${detailed}: ${event.errorDescription || ""}`,
    error: `CAF Media Error - ${event.errorDescription || detailed}`,
  } as PlaybackEvent)
})

on(cast.framework.events.EventType.LOADED_METADATA, () => {
  const info = playerManager.getMediaInformation()
  castDebugLogger.debug(
    TAG_CAF,
    `LOADED_METADATA contentId=${info?.contentId ?? "—"} type=${info?.contentType ?? "—"}`,
  )
})

on(cast.framework.events.EventType.BUFFERING, () => {
  bufferedLog("debug", TAG_CAF, "BUFFERING")
})

on(cast.framework.events.EventType.STALLED, () => {
  // Log only — STALLED fires on minor buffer hitches that recover on their
  // own.  Acting on every one churns the player.  The watchdog below decides
  // when a stall is "real" (sustained no-advance).
  const t = playerManager.getCurrentTimeSec()
  bufferedLog("warn", TAG_CAF, `STALLED at ${Number.isFinite(t) ? t.toFixed(2) : "?"}s`)
})

on(
  cast.framework.events.EventType.DURATION_CHANGE,
  (event: cast.framework.events.DurationChangeEvent) => {
    bufferedLog("debug", TAG_CAF, `DURATION_CHANGE ${event.duration}`)
  },
)

on(cast.framework.events.EventType.TIME_UPDATE, (event: cast.framework.events.TimeUpdateEvent) => {
  // Stall detection no longer lives here — TIME_UPDATE goes silent during a
  // freeze, so the watchdog runs on its own interval (see stallWatchdogTick).
  playbackActor.send({ type: "TIME_UPDATE", currentTime: event.currentMediaTime } as PlaybackEvent)
  maybeAutoSkip(event.currentMediaTime)
})

on(
  cast.framework.events.EventType.BITRATE_CHANGED,
  (event: cast.framework.events.BitrateChangedEvent) => {
    bufferedLog("debug", TAG_CAF, `BITRATE_CHANGED ${event.totalBitrate}`)
  },
)

on(cast.framework.events.EventType.QUEUE_CHANGED, () => {
  bufferedLog("debug", TAG_CAF, "QUEUE_CHANGED")
  playbackActor.send({ type: "QUEUE_CHANGED" } as PlaybackEvent)
})

on(cast.framework.events.EventType.LOAD_CANCELLED, () => {
  bufferedLog("warn", TAG_CAF, "LOAD_CANCELLED")
  playbackActor.send({ type: "LOAD_CANCELLED" } as PlaybackEvent)
})

// ── Custom message handler (dev commands) ────────────────────────────────

let isOverlayVisible = false

context.addCustomMessageListener(
  "urn:x-cast:com.projektor.debug",
  (request: cast.framework.messages.CustomMessageEvent) => {
    const action = request.data?.action

    switch (action) {
      case "toggle-debug":
        bufferedLog("debug", TAG_DEV, "toggle-debug")
        isOverlayVisible = !isOverlayVisible
        castDebugLogger.showDebugLogs(isOverlayVisible)
        break

      case "dump-state": {
        bufferedLog("debug", TAG_DEV, "dump-state")
        const snapshot = playbackActor.getSnapshot()
        const sv = snapshot.value as { lifecycle: string; playback: string }
        castDebugLogger.debug(
          TAG_DEV,
          `state lifecycle=${sv.lifecycle} playback=${sv.playback} epoch=${snapshot.context.epoch} item=${
            snapshot.context.activeSession?.data.jellyfinItemID ?? "—"
          }`,
        )
        break
      }

      case "reset-receiver":
        bufferedLog("warn", TAG_DEV, "reset-receiver requested")
        playbackActor.send({ type: "RESET" } as PlaybackEvent)
        break

      case "force-progress-error": {
        bufferedLog("warn", TAG_DEV, "force-progress-error set (next progress will fail)")
        const ctxProgress = playbackActor.getSnapshot()
        if (ctxProgress.context.progressReporter) {
          ctxProgress.context.progressReporter.setForceNextError(true)
        }
        break
      }

      case "dump-logs":
        bufferedLog("debug", TAG_DEV, "dump-logs")
        dumpLogBuffer()
        break

      case "clear-logs":
        bufferedLog("debug", TAG_DEV, "clear-logs")
        logBuffer.length = 0
        castDebugLogger.clearDebugLogs()
        break

      default:
        if (action) {
          bufferedLog("warn", TAG_DEV, `Unknown action: ${action}`)
        }
    }
  },
)

// ── Custom message handler (control channel) ──────────────────────────────
//
// Track-switching is gone — track changes are full LOAD reloads driven by the
// sender.  The channel now carries `set-autoskip` (live auto-skip preference
// updates) and stays attached for `load-ready` epoch broadcasts the sender
// listens for.  Stale-epoch messages are dropped (queue-advance races).
context.addCustomMessageListener(
  "urn:x-cast:com.projektor.control",
  (request: cast.framework.messages.CustomMessageEvent) => {
    const msg = request.data as
      | {
        action?: string
        epoch?: number
        skipIntro?: boolean
        skipOutroNext?: boolean
        introDelaySec?: number
        outroDelaySec?: number
      }
      | undefined
    const action = msg?.action
    if (!action) return

    const ctx = playbackActor.getSnapshot().context
    if (typeof msg?.epoch === "number" && msg.epoch !== ctx.epoch) {
      castDebugLogger.warn(
        TAG_CONTROL,
        `${action} dropped — stale epoch msgEpoch=${msg.epoch} current=${ctx.epoch}`,
      )
      return
    }

    // Live auto-skip preference update.  Becomes authoritative over the
    // customData seed for the rest of the receiver's lifetime.
    if (action === "set-autoskip") {
      // Capture prior values so the toast only announces real flips, not
      // no-op re-sends.  Labels mirror the app's Auto-Skip settings screen.
      const changes: string[] = []
      if (typeof msg?.skipIntro === "boolean" && msg.skipIntro !== autoSkipPrefs.skipIntro) {
        autoSkipPrefs.skipIntro = msg.skipIntro
        changes.push(`Skip Intros: ${msg.skipIntro ? "On" : "Off"}`)
      }
      if (
        typeof msg?.skipOutroNext === "boolean" &&
        msg.skipOutroNext !== autoSkipPrefs.skipOutroNext
      ) {
        autoSkipPrefs.skipOutroNext = msg.skipOutroNext
        changes.push(`Skip to Next on Outro: ${msg.skipOutroNext ? "On" : "Off"}`)
      }
      if (typeof msg?.introDelaySec === "number" && msg.introDelaySec !== autoSkipPrefs.introDelaySec) {
        autoSkipPrefs.introDelaySec = msg.introDelaySec
        changes.push(`Intro Skip Delay: ${msg.introDelaySec}s`)
      }
      if (typeof msg?.outroDelaySec === "number" && msg.outroDelaySec !== autoSkipPrefs.outroDelaySec) {
        autoSkipPrefs.outroDelaySec = msg.outroDelaySec
        changes.push(`Outro Advance Delay: ${msg.outroDelaySec}s`)
      }
      autoSkipPrefs.prefsFromControl = true
      if (changes.length) showToast(changes.join("   "))
      castDebugLogger.info(
        TAG_CONTROL,
        `set-autoskip skipIntro=${autoSkipPrefs.skipIntro} skipOutroNext=${autoSkipPrefs.skipOutroNext} ` +
          `introDelay=${autoSkipPrefs.introDelaySec}s outroDelay=${autoSkipPrefs.outroDelaySec}s`,
      )
      return
    }

    bufferedLog("debug", TAG_CONTROL, `${action} received (no-op)`)
  },
)

// ── Initialize receiver ────────────────────────────────────────────────────

// Use the default Cast Media Player Library (MPL), not Shaka.  Per device
// probe (canDisplayType) the Chromecast Ultra accepts HEVC Main10 4K with
// `hvc1` codec string; Shaka's HLS player rejects that string.  Default MPL
// handles HLS+TS — which is the Jellyfin transcoding default.
context.start()

// Start the stall watchdog (see "Stall detection + recovery" block above).
// Independent of CAF events so it keeps polling when TIME_UPDATE goes silent.
setInterval(stallWatchdogTick, WATCHDOG_INTERVAL_MS)

// playerManager.setTextTracksStyle({ fontScale: 0.75 })
// Disabled 2026-05-15: throws "setTextTracksStyle is not a function" on
// current CAF SDK — method doesn't exist on PlayerManager.  If we want a
// custom text track style, set it via MediaInformation.textTrackStyle in
// the LOAD interceptor instead.
